import { useState, useEffect } from 'react'
import { Loader2, Plus, Trash2, Edit2, Star, Check, X, RefreshCw } from 'lucide-react'
import toast from 'react-hot-toast'
import { api, RejectionTemplate, CreateRejectionTemplateDto, UpdateRejectionTemplateDto } from '@/lib/api'
import { cn } from '@/lib/utils'
import { useTheme } from '@/contexts/ThemeContext'

export function TemplatesPage() {
  const { theme } = useTheme()
  const [templates, setTemplates] = useState<RejectionTemplate[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [editingTemplate, setEditingTemplate] = useState<RejectionTemplate | null>(null)
  const [isSubmitting, setIsSubmitting] = useState(false)

  // Form state
  const [formName, setFormName] = useState('')
  const [formContent, setFormContent] = useState('')
  const [formIsDefault, setFormIsDefault] = useState(false)

  const fetchTemplates = async () => {
    console.log('[TemplatesPage] Cargando plantillas...')
    setIsLoading(true)
    try {
      const data = await api.getRejectionTemplates(true)
      console.log('[TemplatesPage] Plantillas cargadas:', data)
      setTemplates(data)
    } catch (error: any) {
      console.error('[TemplatesPage] Error al cargar plantillas:', error)
      toast.error(error.message || 'Error al cargar plantillas')
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    fetchTemplates()
  }, [])

  const resetForm = () => {
    setFormName('')
    setFormContent('')
    setFormIsDefault(false)
    setEditingTemplate(null)
    setShowCreateModal(false)
  }

  const openEditModal = (template: RejectionTemplate) => {
    setEditingTemplate(template)
    setFormName(template.name)
    setFormContent(template.content)
    setFormIsDefault(template.isDefault)
    setShowCreateModal(true)
  }

  const handleSubmit = async () => {
    if (!formName.trim() || !formContent.trim()) {
      toast.error('Nombre y contenido son requeridos')
      return
    }

    setIsSubmitting(true)
    try {
      if (editingTemplate) {
        // Update
        const dto: UpdateRejectionTemplateDto = {
          name: formName,
          content: formContent,
          isDefault: formIsDefault,
        }
        await api.updateRejectionTemplate(editingTemplate.id, dto)
        toast.success('Plantilla actualizada')
      } else {
        // Create
        const dto: CreateRejectionTemplateDto = {
          name: formName,
          content: formContent,
          isDefault: formIsDefault,
        }
        await api.createRejectionTemplate(dto)
        toast.success('Plantilla creada')
      }
      resetForm()
      fetchTemplates()
    } catch (error: any) {
      toast.error(error.message || 'Error al guardar plantilla')
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleDelete = async (id: string) => {
    if (!confirm('Seguro que deseas eliminar esta plantilla?')) return

    try {
      await api.deleteRejectionTemplate(id)
      toast.success('Plantilla eliminada')
      fetchTemplates()
    } catch (error: any) {
      toast.error(error.message || 'Error al eliminar')
    }
  }

  const handleSetDefault = async (id: string) => {
    try {
      await api.setDefaultRejectionTemplate(id)
      toast.success('Plantilla marcada como predeterminada')
      fetchTemplates()
    } catch (error: any) {
      toast.error(error.message || 'Error al marcar como predeterminada')
    }
  }

  const handleToggleActive = async (template: RejectionTemplate) => {
    try {
      await api.updateRejectionTemplate(template.id, { isActive: !template.isActive })
      toast.success(template.isActive ? 'Plantilla desactivada' : 'Plantilla activada')
      fetchTemplates()
    } catch (error: any) {
      toast.error(error.message || 'Error al cambiar estado')
    }
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className={cn(
            'text-xl font-semibold',
            theme === 'dark' ? 'text-white' : 'text-gray-900'
          )}>Plantillas de Rechazo</h1>
          <p className={cn(
            'text-sm mt-1',
            theme === 'dark' ? 'text-dark-400' : 'text-gray-500'
          )}>
            Configura plantillas predefinidas para los motivos de rechazo
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={fetchTemplates}
            className={cn(
              'p-2 rounded-lg transition-colors',
              theme === 'dark'
                ? 'hover:bg-dark-800 text-dark-400 hover:text-white'
                : 'hover:bg-gray-100 text-gray-400 hover:text-gray-900'
            )}
            title="Actualizar"
          >
            <RefreshCw className={`w-4 h-4 ${isLoading ? 'animate-spin' : ''}`} />
          </button>
          <button
            onClick={() => setShowCreateModal(true)}
            className="btn-primary btn-sm flex items-center gap-1"
          >
            <Plus className="w-4 h-4" />
            Nueva Plantilla
          </button>
        </div>
      </div>

      {isLoading && templates.length === 0 && (
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-uni-500" />
        </div>
      )}

      {!isLoading && templates.length === 0 && (
        <div className={cn(
          'rounded-2xl border p-8 text-center',
          theme === 'dark'
            ? 'bg-dark-900 border-dark-700'
            : 'bg-white border-gray-200'
        )}>
          <p className={cn(
            'mb-4',
            theme === 'dark' ? 'text-dark-400' : 'text-gray-500'
          )}>No hay plantillas configuradas</p>
          <button
            onClick={() => setShowCreateModal(true)}
            className="btn-primary btn-sm"
          >
            Crear primera plantilla
          </button>
        </div>
      )}

      {templates.length > 0 && (
        <div className="grid gap-3">
          {templates.map((template) => (
            <div
              key={template.id}
              className={cn(
                'rounded-2xl border p-4 transition-all',
                theme === 'dark'
                  ? 'bg-dark-900 border-dark-700'
                  : 'bg-white border-gray-200',
                !template.isActive && 'opacity-50',
                template.isDefault && 'border-yellow-500/30'
              )}
            >
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <h3 className={cn(
                      'font-medium',
                      theme === 'dark' ? 'text-white' : 'text-gray-900'
                    )}>{template.name}</h3>
                    {template.isDefault && (
                      <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-yellow-500/20 text-yellow-400 text-xs">
                        <Star className="w-3 h-3 mr-1" />
                        Predeterminada
                      </span>
                    )}
                    {!template.isActive && (
                      <span className={cn(
                        'inline-flex items-center px-2 py-0.5 rounded-full text-xs',
                        theme === 'dark'
                          ? 'bg-dark-600 text-dark-400'
                          : 'bg-gray-200 text-gray-500'
                      )}>
                        Inactiva
                      </span>
                    )}
                  </div>
                  <p className={cn(
                    'text-sm whitespace-pre-wrap line-clamp-2',
                    theme === 'dark' ? 'text-dark-400' : 'text-gray-500'
                  )}>
                    {template.content}
                  </p>
                </div>
                <div className="flex items-center gap-1 flex-shrink-0">
                  {!template.isDefault && template.isActive && (
                    <button
                      onClick={() => handleSetDefault(template.id)}
                      className={cn(
                        'p-2 rounded-lg transition-colors',
                        theme === 'dark'
                          ? 'hover:bg-dark-700 text-dark-400 hover:text-yellow-400'
                          : 'hover:bg-gray-100 text-gray-400 hover:text-yellow-500'
                      )}
                      title="Marcar como predeterminada"
                    >
                      <Star className="w-4 h-4" />
                    </button>
                  )}
                  <button
                    onClick={() => handleToggleActive(template)}
                    className={cn(
                      'p-2 rounded-lg transition-colors',
                      template.isActive ? 'text-green-400' : (theme === 'dark' ? 'text-dark-400' : 'text-gray-400'),
                      theme === 'dark' ? 'hover:bg-dark-700' : 'hover:bg-gray-100'
                    )}
                    title={template.isActive ? 'Desactivar' : 'Activar'}
                  >
                    {template.isActive ? <Check className="w-4 h-4" /> : <X className="w-4 h-4" />}
                  </button>
                  <button
                    onClick={() => openEditModal(template)}
                    className={cn(
                      'p-2 rounded-lg transition-colors',
                      theme === 'dark'
                        ? 'hover:bg-dark-700 text-dark-400 hover:text-uni-400'
                        : 'hover:bg-gray-100 text-gray-400 hover:text-uni-500'
                    )}
                    title="Editar"
                  >
                    <Edit2 className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => handleDelete(template.id)}
                    className={cn(
                      'p-2 rounded-lg transition-colors',
                      theme === 'dark'
                        ? 'hover:bg-dark-700 text-dark-400 hover:text-red-400'
                        : 'hover:bg-gray-100 text-gray-400 hover:text-red-500'
                    )}
                    title="Eliminar"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Create/Edit Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className={cn(
            'rounded-2xl border p-6 max-w-lg w-full space-y-4',
            theme === 'dark'
              ? 'bg-dark-900 border-dark-700'
              : 'bg-white border-gray-200'
          )}>
            <h3 className={cn(
              'text-lg font-semibold',
              theme === 'dark' ? 'text-white' : 'text-gray-900'
            )}>
              {editingTemplate ? 'Editar Plantilla' : 'Nueva Plantilla'}
            </h3>

            <div>
              <label className={cn(
                'block text-sm mb-1',
                theme === 'dark' ? 'text-dark-400' : 'text-gray-500'
              )}>Nombre</label>
              <input
                type="text"
                value={formName}
                onChange={(e) => setFormName(e.target.value)}
                placeholder="Ej: KYC Incompleto"
                className={cn(
                  'w-full px-3 py-2 rounded-lg border outline-none transition-colors',
                  theme === 'dark'
                    ? 'bg-dark-800 border-dark-600 text-white focus:border-uni-500'
                    : 'bg-gray-50 border-gray-200 text-gray-900 focus:border-uni-500'
                )}
              />
            </div>

            <div>
              <label className={cn(
                'block text-sm mb-1',
                theme === 'dark' ? 'text-dark-400' : 'text-gray-500'
              )}>Contenido</label>
              <textarea
                value={formContent}
                onChange={(e) => setFormContent(e.target.value)}
                placeholder="Texto completo del motivo de rechazo..."
                className={cn(
                  'w-full px-3 py-2 rounded-lg border outline-none transition-colors min-h-[120px] resize-none',
                  theme === 'dark'
                    ? 'bg-dark-800 border-dark-600 text-white focus:border-uni-500'
                    : 'bg-gray-50 border-gray-200 text-gray-900 focus:border-uni-500'
                )}
              />
            </div>

            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={formIsDefault}
                onChange={(e) => setFormIsDefault(e.target.checked)}
                className={cn(
                  'w-4 h-4 rounded text-uni-500 focus:ring-uni-500',
                  theme === 'dark'
                    ? 'border-dark-600 bg-dark-800'
                    : 'border-gray-300 bg-white'
                )}
              />
              <span className={cn(
                'text-sm',
                theme === 'dark' ? 'text-dark-300' : 'text-gray-600'
              )}>Marcar como predeterminada</span>
            </label>

            <div className="flex gap-3 justify-end pt-2">
              <button
                onClick={resetForm}
                disabled={isSubmitting}
                className={cn(
                  'px-4 py-2 rounded-lg text-sm font-medium transition-colors',
                  theme === 'dark'
                    ? 'bg-dark-700 text-dark-300 hover:bg-dark-600'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                )}
              >
                Cancelar
              </button>
              <button
                onClick={handleSubmit}
                disabled={isSubmitting || !formName.trim() || !formContent.trim()}
                className="btn-primary btn-sm"
              >
                {isSubmitting ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : editingTemplate ? (
                  'Guardar Cambios'
                ) : (
                  'Crear Plantilla'
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
